/**
 * Billing Store
 *
 * Centralized state management for billing information using Zustand.
 * Prevents duplicate API calls and provides shared state across components.
 */

import { create } from 'zustand';
import { getBillingInfo, BillingInfo } from '../lib/api';

interface BillingStore {
  billingInfo: BillingInfo | null;
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  fetchBillingInfo: (force?: boolean) => Promise<void>;
  clearBillingInfo: () => void;
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Track in-flight requests to prevent duplicates
let fetchingPromise: Promise<void> | null = null;

export const useBillingStore = create<BillingStore>((set, get) => ({
  billingInfo: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchBillingInfo: async (force = false) => {
    const { lastFetched, isLoading } = get();

    // Return cached data if still fresh and not forcing refresh
    if (!force && lastFetched && Date.now() - lastFetched < CACHE_DURATION) {
      return;
    }

    // If a request is already in flight, wait for it
    if (fetchingPromise) {
      return fetchingPromise;
    }

    // Prevent duplicate concurrent requests
    if (isLoading) {
      return;
    }

    // Create the fetching promise
    fetchingPromise = (async () => {
      set({ isLoading: true, error: null });
      try {
        const info = await getBillingInfo();
        set({
          billingInfo: info,
          lastFetched: Date.now(),
          isLoading: false,
          error: null,
        });
      } catch (error) {
        set({
          isLoading: false,
          error: error as Error,
        });
        throw error;
      } finally {
        fetchingPromise = null;
      }
    })();

    return fetchingPromise;
  },

  clearBillingInfo: () => {
    set({ billingInfo: null, lastFetched: null, error: null });
  },
}));
